/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import React, {useEffect} from 'react';
import PlansHeader from './PlansHeader';
import styled from 'styled-components';
import ProvisioningTabMenu from '../provisioning/ProvisioningTabMenu';
import {FLEET_MAP_ID, setMapHidden} from '../../store/maps/actions';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {useRouteMatch} from 'react-router-dom';
import {Authorities} from '../../shared/entities/user';
import {selectUserData} from '../../store';
import {areAllowedRolesPresent} from '../../utils/RBACUtils';
import MissionPlans from './MissionPlans/MissionPlans';
import HnaRequests from "./HnaRequest/HnaRequests";
import {ContainerBox} from '../provisioning/ProvisioningContainer';

const StyleWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export interface TabConfig {
  // The title of the tab
  title: string;
  // Route to sub-view
  route: string;
  // The content to display when the tab is chosen
  content: JSX.Element;
  // Tab is displayed if this value is unset, or if the user has any of these roles
  restrictedToRoles?: Authorities[];
}

const PlansContainer = () => {
  const dispatch = useDispatch();
  const { path } = useRouteMatch();
  const userData = useSelector(selectUserData, shallowEqual);

  // If you need to add a new tab add a TabConfig definition below
  const PLANS_TABS: TabConfig[] = [
    {
      title: 'Mission Plans',
      route: 'plans',
      content: <MissionPlans />,
      restrictedToRoles: [Authorities.ROLE_MISSION_PLANS_VIEW],
    },
    {
      title: 'HNA Requests',
      route: 'hna-requests',
      content: <HnaRequests />,
      restrictedToRoles: [Authorities.ROLE_MISSION_PLANS_VIEW]
    }
  ];

  /**
   * Returns true if a tab should be displayed based on TabConfig above, and the logged-in users' list of roles.
   * @param tabConfig TabConfig for current tab.
   */
  const allowedForRole = (tabConfig: TabConfig) => {
    // If not configured, then tab is viewable by all.
    if (!tabConfig.restrictedToRoles) return true;

    // If userData is missing, then tab is not viewable.
    if (!userData) return false;

    // Otherwise, visibility is determined by roles in JWT.
    return areAllowedRolesPresent(userData, ...tabConfig.restrictedToRoles);
  };

  // only show tabs allowed for the current user
  const tabsAllowedForCurrentUser = PLANS_TABS.filter(allowedForRole);

  useEffect(() => {
    // hide the fleet/FTV view map in the case of refresh on page
    dispatch(setMapHidden(FLEET_MAP_ID)(true));
  }, [dispatch]);

  return (
    <StyleWrapper>
      <PlansHeader />
      <ProvisioningTabMenu tabs={tabsAllowedForCurrentUser} />
      <ContainerBox path={path} tabsAllowedForCurrentUser={tabsAllowedForCurrentUser} />
    </StyleWrapper>
  );
};

export default PlansContainer;